<template>
  <!-- Main Content -->
  <main class="py-4 sm:py-8 sm:p-8">
    <!-- Header -->
    <div class="mb-10 text-start">
      <PageTitle>
        Minhas Vendas
      </PageTitle>
      <PageDescription>
        Acompanhe suas vendas de maneira estruturada, obtenha insights para aprimorar sua administração!
      </PageDescription>
    </div>

    <!-- Filters and Table Card -->
    <TableContainer>
      <!-- Filters -->
      <div class="mb-8 flex flex-wrap items-center gap-4">
        <h2 class="mr-4 text-xl font-semibold text-white">Vendas realizadas</h2>

        <div class="flex flex-1 flex-wrap items-center gap-4">
          <!-- Date Filters -->
          <div class="flex items-center flex-wrap gap-4">
            <Popover>
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  class="w-[240px] justify-start text-left font-normal"
                >
                  <CalendarIcon class="mr-2 h-4 w-4" />
                  {{ from ? format(from, 'PPP', { locale: ptBR }) : 'Selecione a data inicial' }}
                </Button>
              </PopoverTrigger>
              <PopoverContent class="w-auto p-0">
                <Calendar
                  mode="single"
                  initialFocus
                  locale="pt-BR"
                  :selected="from"
                  @update:model-value="(date: any) => {
                    from = new Date(date.year, date.month - 1, date.day);
                  }"
                  :disabled-dates="{ before: new Date() }"
                />
              </PopoverContent>
            </Popover>

            <span class="text-gray-500">até</span>

            <Popover>
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  class="w-[240px] justify-start text-left font-normal"
                >
                  <CalendarIcon class="mr-2 h-4 w-4" />
                  {{ to ? format(to, 'PPP', { locale: ptBR }) : 'Selecione a data final' }}
                </Button>
              </PopoverTrigger>
              <PopoverContent class="w-auto p-0">
                <Calendar
                  mode="single"
                  initialFocus
                  locale="pt-BR"
                  :selected="to"
                  @update:model-value="(date: any) => {
                    to = new Date(date.year, date.month - 1, date.day);
                  }"
                  :disabled-dates="{ before: new Date() }"
                />
              </PopoverContent>
            </Popover>
          </div>
        </div>

        <div>
          <Select v-model="paymentStatus">
            <SelectTrigger class="w-[180px]">
              <SelectValue placeholder="Todos" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos os tipos</SelectItem>
              <SelectItem v-for="status in OrderPaymentStatus" :value="status">{{ OrderPaymentStatusLabels[status] }}</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <div>
           <Select v-model="botId">
            <SelectTrigger class="w-[180px]">
              <SelectValue placeholder="Bot" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos os bots</SelectItem>
              <SelectItem v-for="bot in bots" :value="String(bot.id)">{{ bot.name }}</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      <!-- Table -->
      <Table class="hidden md:block">
        <TableHeader>
          <TableRow>
            <TableHead 
              v-for="header in tableHeaders"
              :key="header.key"
              :class="header.class"
            >
              <div class="flex items-center gap-2">
                {{ header.label }}
                <ArrowsUpDownIcon
                  v-if="header.sortable"
                  class="h-4 w-4 cursor-pointer text-gray-500 hover:text-white"
                  @click="sortBy(header.key)"
                />
              </div>
            </TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          <template v-if="orders">
            <TableFallback v-if="orders.data.length === 0" :colspan="tableHeaders.length" :icon="ArrowsUpDownIcon" title="Nenhum venda encontrado" :description="`Tente ajustar seus filtros ou volte mais tarde`" />
            <TableRow
              v-for="order in orders.data"
              :key="order.id"
            >
              <TableCell class="whitespace-nowrap text-sm font-medium text-emerald-400">
                #{{ order.id }}
              </TableCell>
              <TableCell class="whitespace-nowrap text-sm font-medium">
                {{ order.customer?.name }}
              </TableCell>
              <TableCell class="whitespace-nowrap text-sm text-gray-300">
                {{ order.bot?.name || '-' }}
              </TableCell>
              <TableCell class="whitespace-nowrap text-sm text-gray-300">
                {{ order.createdAt.toString().split('T')[0].split('-').reverse().join('/') }}
              </TableCell>
              <TableCell class="whitespace-nowrap text-sm text-gray-300">
                <div class="flex flex-wrap gap-2">
                  <Badge 
                    variant="default"
                  >
                    {{ order.paymentPlan?.name }}
                  </Badge>
                </div>
              </TableCell>
              <TableCell class="text-sm text-gray-300 flex flex-col gap-1">
                <div>
                  <PaymentStatusBadge :status="order.paymentStatus" />
                </div>
                <span class="flex items-center gap-2">
                  <svg v-if="order.paymentMethod === OrderPaymentMethod.PIX" xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                    <path d="M16.5098 5.01L14.5498 3.05C13.1498 1.65 10.8498 1.65 9.44977 3.05L7.48977 5.01C7.09977 5.4 7.09977 6.04 7.48977 6.43L11.2998 10.24C11.6898 10.63 12.3198 10.63 12.7098 10.24L16.5198 6.43C16.8998 6.04 16.8998 5.4 16.5098 5.01Z" fill="#fff"/>
                    <path d="M5.01 7.49L3.05 9.45C1.65 10.85 1.65 13.15 3.05 14.55L5.01 16.51C5.4 16.9 6.03 16.9 6.42 16.51L10.23 12.7C10.62 12.31 10.62 11.68 10.23 11.29L6.43 7.49C6.04 7.1 5.4 7.1 5.01 7.49Z" fill="#fff"/>
                    <path d="M20.95 9.45L18.99 7.49C18.6 7.1 17.97 7.1 17.58 7.49L13.77 11.3C13.38 11.69 13.38 12.32 13.77 12.71L17.58 16.52C17.97 16.91 18.6 16.91 18.99 16.52L20.95 14.56C22.35 13.15 22.35 10.85 20.95 9.45Z" fill="#fff"/>
                    <path d="M7.49005 18.99L9.45005 20.95C10.8501 22.35 13.1501 22.35 14.5501 20.95L16.5101 18.99C16.9001 18.6 16.9001 17.97 16.5101 17.58L12.7001 13.77C12.3101 13.38 11.6801 13.38 11.2901 13.77L7.48005 17.58C7.10005 17.96 7.10005 18.6 7.49005 18.99Z" fill="#fff"/>
                  </svg>
                  <svg v-if="order.paymentMethod === OrderPaymentMethod.CREDIT_CARD" xmlns="http://www.w3.org/2000/svg" width="25" height="24" viewBox="0 0 25 24" fill="none">
                    <path d="M22.0317 7.55002C22.0317 8.21002 21.4917 8.75002 20.8317 8.75002H3.23174C2.57174 8.75002 2.03174 8.21002 2.03174 7.55002V7.54002C2.03174 5.25002 3.88174 3.40002 6.17174 3.40002H17.8817C20.1717 3.40002 22.0317 5.26002 22.0317 7.55002Z" fill="#fff"/>
                    <path d="M2.03174 11.45V16.46C2.03174 18.75 3.88174 20.6 6.17174 20.6H17.8817C20.1717 20.6 22.0317 18.74 22.0317 16.45V11.45C22.0317 10.79 21.4917 10.25 20.8317 10.25H3.23174C2.57174 10.25 2.03174 10.79 2.03174 11.45ZM8.03174 17.25H6.03174C5.62174 17.25 5.28174 16.91 5.28174 16.5C5.28174 16.09 5.62174 15.75 6.03174 15.75H8.03174C8.44174 15.75 8.78174 16.09 8.78174 16.5C8.78174 16.91 8.44174 17.25 8.03174 17.25ZM14.5317 17.25H10.5317C10.1217 17.25 9.78174 16.91 9.78174 16.5C9.78174 16.09 10.1217 15.75 10.5317 15.75H14.5317C14.9417 15.75 15.2817 16.09 15.2817 16.5C15.2817 16.91 14.9417 17.25 14.5317 17.25Z" fill="#fff"/>
                  </svg>
                  {{ OrderPaymentMethodLabels[order.paymentMethod] }}
                </span>
              </TableCell>
              <TableCell class="whitespace-nowrap text-sm font-medium text-emerald-400">
                {{ formatCurrency(order.amount) }}
              </TableCell>
              <TableCell class="whitespace-nowrap text-right text-sm">
                <button
                  class="rounded-lg bg-white/5 p-2 text-gray-400 opacity-0 transition-all hover:bg-white/10 hover:text-white group-hover:opacity-100"
                  @click="viewSaleDetails(order)"
                >
                  <EyeIcon class="h-4 w-4" />
                </button>
              </TableCell>
            </TableRow>
          </template>
          <TableRowSkeleton v-else :columns="tableHeaders.length" />
        </TableBody>
      </Table>

      <!-- Mobile List -->
      <div class="space-y-4 md:hidden text-left" v-if="orders">
        <template v-if="orders.data.length > 0">
          <MobileCard v-for="order in orders.data" :key="order.id">
            <template #header>
               <div class="flex items-center gap-2">
                 <div class="flex flex-col">
                  <span class="text-[10px] uppercase font-bold text-zinc-500 tracking-wider">Pedido</span>
                  <div class="flex items-center gap-2">
                    <span class="text-white font-bold text-lg">#{{ order.id }}</span>
                    <div class="h-2 w-2 rounded-full bg-emerald-500"></div>
                  </div>
                 </div>
               </div>
            </template>
            
            <template #actions>
               <!-- <div class="flex flex-col items-end">
                  <span class="text-[10px] uppercase font-bold text-zinc-500 tracking-wider mb-1">Visualizar</span>
                  <button @click="viewSaleDetails(order)" class="text-white hover:text-emerald-400 p-1">
                    <EyeIcon class="size-5" />
                  </button>
               </div> -->
            </template>
            
            <template #content>
               <MobileDataLabel label="Cliente">
                  <div class="font-bold text-white">{{ order.customer?.name }}</div>
                  <div class="text-zinc-500 text-xs truncate max-w-[250px]">{{ order.customer?.email }}</div>
               </MobileDataLabel>
               
               <MobileDataLabel label="Datas">
                  <div class="text-xs">
                    <span class="text-zinc-400">Criação:</span> {{ order.createdAt ? new Date(order.createdAt).toLocaleString('pt-BR') : '-' }}
                  </div>
               </MobileDataLabel>

               <MobileDataLabel label="Produtos">
                  {{ order.bot?.name || '-' }}
               </MobileDataLabel>
               
               <div class="flex items-center justify-between pt-2">
                  <div class="flex flex-col">
                    <span class="text-[10px] uppercase font-bold text-zinc-500 tracking-wider mb-1">Status</span>
                    <PaymentStatusBadge :status="order.paymentStatus" />
                  </div>
                  <div class="flex flex-col items-end">
                    <span class="text-[10px] uppercase font-bold text-zinc-500 tracking-wider mb-1">Valor Final</span>
                    <div class="text-xl font-bold text-white">{{ formatCurrency(order.amount) }}</div>
                  </div>
               </div>
            </template>
          </MobileCard>
        </template>
        <div v-else class="text-center py-12 text-zinc-500">
           <ArrowsUpDownIcon class="size-8 mx-auto mb-3 text-zinc-700" />
           Nenhum venda encontrado
        </div>
      </div>


      <!-- Pagination -->
      <Pagination @update:page="page => reloadAndGetAll({ page, to, from })" v-if="orders?.pagination" :data="orders?.pagination" v-model:per-page="perPage" />
    </TableContainer>
  </main>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, watch } from 'vue'
import {
  CalendarIcon,
  ArrowsUpDownIcon,
  EyeIcon,
} from '@heroicons/vue/24/outline'
import { format } from 'date-fns'
import { ptBR } from 'date-fns/locale'
import { Calendar } from '@/components/ui/calendar'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Input } from '@/components/ui/input'
import { Button } from '@/components/ui/button'
import Table from '@/components/ui/table/Table.vue'
import TableHeader from '@/components/ui/table/TableHeader.vue'
import TableRow from '@/components/ui/table/TableRow.vue'
import TableHead from '@/components/ui/table/TableHead.vue'
import TableBody from '@/components/ui/table/TableBody.vue'
import TableFallback from '@/components/ui/table/TableFallback.vue'
import TableCell from '@/components/ui/table/TableCell.vue'
import Pagination from '@/components/ui/pagination/Pagination.vue'
import TableContainer from '@/components/ui/table/TableContainer.vue'
import { GetAllOrdersParams, useOrder } from '@/composables/useOrder'
import { Pagination as IPagination } from '@/interfaces/Pagination'
import { useBot } from '@/composables/useBot'
import { Bot } from '@/interfaces/Bot'
import TableRowSkeleton from '@/components/ui/table/TableRowSkeleton.vue'
import { Order } from '@/interfaces/orders/Order'
import { OrderPaymentStatus, OrderPaymentStatusLabels } from '@/enums/orders/OrderPaymentStatus'
import { OrderPaymentMethod, OrderPaymentMethodLabels } from '@/enums/orders/OrderPaymentMethod'
import PageTitle from '@/components/ui/page/PageTitle.vue'
import PageDescription from '@/components/ui/page/PageDescription.vue'
import { Badge } from '@/components/ui/badge'
import PaymentStatusBadge from '@/components/ui/badge/PaymentStatusBadge.vue'
import MobileCard from '@/components/ui/mobile-card/MobileCard.vue'
import MobileDataLabel from '@/components/ui/mobile-card/MobileDataLabel.vue'

// Filters
const paymentStatus = ref<OrderPaymentStatus>()
const botId = ref<string>('all')
const from = ref<Date>()
const to = ref<Date>()
const perPage = ref('10')

// Table Configuration
const tableHeaders = [
  { key: 'id', label: '#', sortable: false, class: 'w-20' },
  { key: 'customer', label: 'Cliente', sortable: false },
  { key: 'bot', label: 'Bot', sortable: false },
  { key: 'date', label: 'Data', sortable: false },
  { key: 'paymentPlan', label: 'Plano de Pagamento', sortable: false },
  { key: 'status', label: 'Status/Método', sortable: false },
  { key: 'value', label: 'Valor', sortable: false, class: 'text-right' },
  { key: 'actions', label: '', class: 'w-20' },
]

const { getAll } = useOrder()
const { getAll: getAllBots } = useBot()
const bots = ref<Bot[]>([])

// Mock orders data (empty for now)
const orders = ref<IPagination<Order>>()

const formatCurrency = (value: number) => {
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL'
  }).format(value)
}

// Actions
const sortBy = (key: string) => {
  console.log('Sorting by:', key)
}

const viewSaleDetails = (order: any) => {
  console.log('Viewing order:', order)
}

const reloadAndGetAll = async (params: GetAllOrdersParams) => {
  orders.value = undefined
  orders.value = await getAll(params)
}

watch([() => from.value, () => to.value, () => perPage.value, () => paymentStatus.value, () => botId.value], () => {
  reloadAndGetAll({ from: from.value, to: to.value, perPage: perPage.value, paymentStatus: (paymentStatus.value as string) === 'all' ? undefined : paymentStatus.value, botId: botId.value === 'all' ? undefined : Number(botId.value) })
})

onMounted(() => {
  getAll().then((data) => orders.value = data)
  getAllBots({ perPage: '1000' }).then((data) => bots.value = data.data)
})
</script>